import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { KEYBOARD_KEYS } from '../../../constants';
import Tag, { TagProps } from '../Tag/Tag';
import { Input, InputWrapper } from './TagInput.styles';
import { isRightKey } from './utils';

export interface TagInputProps {
  /**
   Array of tags
   **/
  tags: string[];
  /**
   * Max number of tags
   */
  maxTags?: number;
  /**
   Placeholder text
   **/
  placeholder: string;
  /**
   Separator between tags used for entering new tags (default: comma)
   **/
  separator?: string;
  /**
   Validate function
   **/
  validationFn?: (value: string) => boolean;
  /**
   Props to customize Tag component
   **/
  tagProps?: Omit<TagProps, 'label' | 'onDelete'>;
  /**
   * OnCreate callback
   */
  onCreate: (value: string) => void;
  /**
   * OnDelete callback
   */
  onRemove: (value: string, index: number) => void;
  /**
   * OnPaste callback
   */
  onPaste: (value: string[]) => void;
  /**
   * OnError blink time
   */
  blinkTime?: number;
  /**
   * Input name
   */
  name?: string;
  /**
   * Input id
   */
  id?: string;
  /**
   * Fullwidth
   */
  fullWidth?: boolean;
}
/**
 - The TagInput component allows the user to enter a list of tags.
 **/
const TagInput = ({
  separator = ',',
  placeholder,
  validationFn,
  tags,
  tagProps,
  onCreate,
  onRemove,
  onPaste,
  blinkTime = 100,
  fullWidth = false,
  maxTags,
  ...rest
}: TagInputProps) => {
  const [isKeyReleased, setIsKeyReleased] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [opacity, setOpacity] = useState(1);
  const [hasError, setHasError] = useState(false);
  const inputEl = React.useRef<HTMLInputElement>(null);
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = e;
    const tag = inputValue.trim();
    setHasError(false);

    if (isRightKey(key, separator) && tag.length && !tags.includes(tag)) {
      e.preventDefault();

      if (!validationFn || validationFn(tag)) {
        onCreate(tag);
        setInputValue('');
      } else {
        setHasError(true);
        setOpacity(0);
        setTimeout(() => setOpacity(1), blinkTime);
      }
    }

    if (
      key === KEYBOARD_KEYS.BACKSPACE &&
      !inputValue.trim().length &&
      tags.length &&
      isKeyReleased
    ) {
      e.preventDefault();
      const tagsCopy = [...tags];
      const poppedTag = tagsCopy.pop();
      onRemove(poppedTag, tags.length - 1);
      setInputValue(poppedTag ?? '');
    }
    setIsKeyReleased(false);
  };

  const handleOnPaste = ({
    clipboardData,
  }: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = clipboardData.getData('Text');
    const parsedText = pastedText.replace(/\s+/g, '');
    const pastedTags = new Set(parsedText.split(separator));

    const maxCountPasted = (maxTags || Infinity) - tags.length;

    const filteredTags = [...pastedTags].filter(
      (t) => !tags.includes(t) && (!validationFn || validationFn(t))
    );
    onPaste(filteredTags.slice(0, maxCountPasted));
    setInputValue('');
  };

  const onKeyUp = () => {
    setIsKeyReleased(true);
  };

  const memoizedTags = useMemo(() => {
    return tags.map((tag, index) => (
      <Tag
        key={index}
        label={tag}
        onDelete={() => onRemove(tag, index)}
        {...tagProps}
      />
    ));
  }, [tags, tagProps, onRemove]);
  const isLimitReached = maxTags && tags.length >= maxTags;
  return (
    <InputWrapper
      fullWidth={fullWidth}
      status={hasError ? 'error' : 'default'}
      size={'regular'}
      onClick={() => inputEl?.current?.focus()}
      data-testid={'tagInput-wrapper'}
    >
      {memoizedTags}
      <motion.div animate={{ opacity }} transition={{ duration: 0.1 }}>
        <Input
          disabled={isLimitReached}
          onPaste={handleOnPaste}
          value={inputValue}
          inputSize={'regular'}
          type={'text'}
          ref={inputEl}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyUp={onKeyUp}
          placeholder={isLimitReached ? '' : placeholder}
          hidden={isLimitReached}
          onKeyDown={onKeyDown}
          {...rest}
        />
      </motion.div>
    </InputWrapper>
  );
};
export default TagInput;
